import { atom, useAtom } from 'jotai'
import { useDontShowAgain } from '~/atoms/DontShowAgain'
import { CallMode } from '~/atoms/SettingsState'
import {
  ContactDetails,
  ContactNumber,
} from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { SpeedDial } from '~/types/WebphoneTypes'

interface ParkingModalState {
  open: boolean
}
interface ExtensionModalState {
  open: boolean
}
interface CallInProgressModalState {
  open: boolean
}

interface FaxModalState {
  open: boolean
  number?: string
  resendId: number
}

interface CallModalState {
  open: boolean
  callee?: string | { contactNumber: ContactNumber; contact: ContactDetails }
  overrideCallMode?: CallMode
  getNumber?: () => string | undefined
}

interface MessagingModalState {
  open: boolean
  number?: string
}

interface MessagingMarkAllReadModalState {
  open: boolean
}

interface PreviewModalState {
  open: boolean
  previewUrl?: string
  previewFilename?: string
}

interface FeedbackModalState {
  open: boolean
}

interface SpeedDialModalState {
  open: boolean
  speedDial?: SpeedDial
}

interface MoveFaxModalState {
  open: boolean
}

interface ConfirmModalState {
  title: string
  message: string | React.ReactNode
  confirmText: string
  cancelText?: string
  closeAfterConfirm?: boolean
  isDestructive?: boolean
  dontShowAgainKey?: string
  onConfirm?: () => void
  onCancel?: () => void
}

interface CustomStatusModalState {
  open: boolean
  customStatus?: {
    id?: number
    name: string
    description: string
    availability: 'Online' | 'Away'
  }
}

interface ModalStates {
  callModal: CallModalState
  extensionModal: ExtensionModalState
  faxModal: FaxModalState
  messagingModal: MessagingModalState
  messagingMarkAllReadModal: MessagingMarkAllReadModalState
  parkingModal: ParkingModalState
  previewModal: PreviewModalState
  confirmModal: ConfirmModalState | null
  callInProgressModal: CallInProgressModalState
  feedbackModal: FeedbackModalState
  speedDialModal: SpeedDialModalState
  moveFaxModal: MoveFaxModalState
  customStatusModal: CustomStatusModalState
}

interface UseModalStateResponse {
  modalState: ModalStates
  activeModalRef: HTMLElement | null
  setActiveModalRef: (newActiveModalRef: HTMLElement | null) => void
  callModal: [CallModalState, (newCallModalState: CallModalState) => void]
  extensionModal: [
    ExtensionModalState,
    (newExtensionModalState: ExtensionModalState) => void,
  ]
  faxModal: [FaxModalState, (newFaxModalState: FaxModalState) => void]
  messagingModal: [
    MessagingModalState,
    (newMessagingModalState: MessagingModalState) => void,
  ]
  messagingMarkAllReadModal: [
    MessagingMarkAllReadModalState,
    (newMessagingMarkAllReadModalState: MessagingMarkAllReadModalState) => void,
  ]
  parkingModal: [
    ParkingModalState,
    (newParkingModalState: ParkingModalState) => void,
  ]
  previewModal: [
    PreviewModalState,
    (newPreviewModalState: PreviewModalState) => void,
  ]

  confirmModal: [
    ConfirmModalState | null,
    (newConfirmModalState: ConfirmModalState | null) => void,
  ]
  callInProgressModal: [
    CallInProgressModalState,
    (newCallInProgressModalState: CallInProgressModalState) => void,
  ]
  feedbackModal: [
    FeedbackModalState,
    (newFeedbackModalState: FeedbackModalState) => void,
  ]
  speedDialModal: [
    SpeedDialModalState,
    (newSpeedDialModalState: SpeedDialModalState) => void,
  ]
  moveFaxModal: [
    MoveFaxModalState,
    (newMoveFaxModalState: MoveFaxModalState) => void,
  ]
  customStatusModal: [
    CustomStatusModalState,
    (newCustomStatusModalState: CustomStatusModalState) => void,
  ]
  isAnyModalOpen: boolean
}

export const faxModalStateDefault: FaxModalState = {
  open: false,
  resendId: 0,
}

const modalStateAtom = atom<ModalStates>({
  callModal: {
    open: false,
  },
  extensionModal: {
    open: false,
  },
  faxModal: Object.assign({}, faxModalStateDefault),
  messagingModal: {
    open: false,
  },
  messagingMarkAllReadModal: {
    open: false,
  },
  previewModal: {
    open: false,
  },
  parkingModal: {
    open: false,
  },
  confirmModal: null,
  callInProgressModal: {
    open: false,
  },
  feedbackModal: {
    open: false,
  },
  speedDialModal: {
    open: false,
  },
  moveFaxModal: {
    open: false,
  },
  customStatusModal: {
    open: false,
  },
})

const activeModalRefAtom = atom<HTMLElement | null>(null)

const useModalState = (): UseModalStateResponse => {
  const [modalState, setModalState] = useAtom(modalStateAtom)
  const [activeModalRef, setActiveModalRef] = useAtom(activeModalRefAtom)
  const [isDontShowAgainSet] = useDontShowAgain()

  const setFaxModalState = (newFaxModalState: FaxModalState) => {
    setModalState({
      ...modalState,
      faxModal: newFaxModalState,
    })
  }

  const setCallModalState = (newCallModalState: CallModalState) => {
    const getNumber = () => {
      if (typeof newCallModalState.callee === 'string') {
        return newCallModalState.callee
      }

      return newCallModalState.callee?.contactNumber.number
    }

    setModalState({
      ...modalState,
      callModal: { ...newCallModalState, getNumber },
    })
  }

  const setExtensionModalState = (
    newExtensionModalState: ExtensionModalState,
  ) => {
    setModalState({
      ...modalState,
      extensionModal: newExtensionModalState,
    })
  }

  const setCallInProgressModalState = (
    newCallInProgressModalState: CallInProgressModalState,
  ) => {
    setModalState({
      ...modalState,
      callInProgressModal: newCallInProgressModalState,
    })
  }

  const setMessagingModalState = (
    newMessagingModalState: MessagingModalState,
  ) => {
    setModalState({
      ...modalState,
      messagingModal: newMessagingModalState,
    })
  }

  const setMessagingMarkAllReadModalState = (
    newMessagingMarkAllReadModalState: MessagingMarkAllReadModalState,
  ) => {
    setModalState({
      ...modalState,
      messagingMarkAllReadModal: newMessagingMarkAllReadModalState,
    })
  }

  const setParkingModalState = (newParkingModalState: ParkingModalState) => {
    setModalState({
      ...modalState,
      parkingModal: newParkingModalState,
    })
  }

  const setPreviewModalState = (newPreviewModalState: PreviewModalState) => {
    setModalState({
      ...modalState,
      previewModal: newPreviewModalState,
    })
  }

  const setFeedbackModalState = (newFeedbackModalState: FeedbackModalState) => {
    setModalState({
      ...modalState,
      feedbackModal: newFeedbackModalState,
    })
  }

  const setConfirmModalState = (
    newConfirmModalState: ConfirmModalState | null,
  ) => {
    if (isDontShowAgainSet(newConfirmModalState?.dontShowAgainKey)) {
      // The user has selected to not show this confirmation again, accept it immediately
      newConfirmModalState?.onConfirm?.()
      return
    }

    setModalState({
      ...modalState,
      confirmModal: newConfirmModalState,
    })
  }

  const setSpeedDialModalState = (
    newSpeedDialModalState: SpeedDialModalState,
  ) => {
    setModalState({
      ...modalState,
      speedDialModal: newSpeedDialModalState,
    })
  }
  const setMoveFaxModalState = (newMoveFaxModalState: MoveFaxModalState) => {
    setModalState({
      ...modalState,
      moveFaxModal: newMoveFaxModalState,
    })
  }

  const setCustomStatusModalState = (
    newCustomStatusModalState: CustomStatusModalState,
  ) => {
    setModalState({
      ...modalState,
      customStatusModal: newCustomStatusModalState,
    })
  }

  const isAnyModalOpen = () => {
    return (
      modalState.faxModal.open ||
      modalState.callModal.open ||
      modalState.messagingModal.open ||
      modalState.messagingMarkAllReadModal.open ||
      modalState.previewModal.open ||
      modalState.callInProgressModal.open ||
      modalState.parkingModal.open ||
      modalState.extensionModal.open ||
      modalState.feedbackModal.open ||
      modalState.confirmModal !== null
    )
  }

  return {
    modalState,
    activeModalRef,
    setActiveModalRef,
    callModal: [modalState.callModal, setCallModalState],
    faxModal: [modalState.faxModal, setFaxModalState],
    extensionModal: [modalState.extensionModal, setExtensionModalState],
    messagingModal: [modalState.messagingModal, setMessagingModalState],
    messagingMarkAllReadModal: [
      modalState.messagingMarkAllReadModal,
      setMessagingMarkAllReadModalState,
    ],
    parkingModal: [modalState.parkingModal, setParkingModalState],
    previewModal: [modalState.previewModal, setPreviewModalState],
    confirmModal: [modalState.confirmModal, setConfirmModalState],
    callInProgressModal: [
      modalState.callInProgressModal,
      setCallInProgressModalState,
    ],
    feedbackModal: [modalState.feedbackModal, setFeedbackModalState],
    speedDialModal: [modalState.speedDialModal, setSpeedDialModalState],
    moveFaxModal: [modalState.moveFaxModal, setMoveFaxModalState],
    customStatusModal: [
      modalState.customStatusModal,
      setCustomStatusModalState,
    ],
    isAnyModalOpen: isAnyModalOpen(),
  }
}

export { useModalState }

export type {
  CallModalState,
  ConfirmModalState,
  ExtensionModalState,
  FaxModalState,
  MessagingModalState,
  ModalStates,
  PreviewModalState,
  UseModalStateResponse,
  FeedbackModalState,
  SpeedDialModalState,
}
